/** @file refitEntry: resizes a facet entry that holds images (its tiles re-uploaded padded or scaled). */
import type { Command } from '@zilo/pose3d-commands/types/command';
import { slotKey } from '@zilo/pose3d-format/derive/slotKey';
import { encodePng } from '@zilo/pose3d-image-io/png/encodePng';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import type { EditorStore } from '../../../state/editorTypes';
import type { ArtDeps, FitMode } from '../artTypes';
import { refitImage } from './refitImage';

/**
 * The resize: drawable and entry, the new `size` and the `pivotFrac` to send (absent when unchanged), the old pixels of
 * every filled tile (by tile index), where the old pivot pixel lands in the new tile minus where it was (`offset`, whole
 * px) and the fit the user chose.
 */
export type RefitPlan = {
  drawable: string; entry: number; size: [number, number]; pivotFrac?: [number, number];
  tiles: { tile: number; pixels: Image }[]; offset: [number, number]; fit: FitMode;
};

const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/**
 * Every tile refitted (`refitImage`), PNG-encoded and uploaded, then one batch labelled `Resize <drawable> (<fit>)`:
 * `facet.setEntry` with the new size (and pivot) and `slots: {}`, then one `slot.fill` per tile with its new image.
 * True when it landed (lint and preview refresh); an error toast and false otherwise (nothing is sent on an upload error).
 */
export async function refitEntry(store: EditorStore, deps: Pick<ArtDeps, 'uploadImage'>, plan: RefitPlan): Promise<boolean> {
  const puppetId = store.getState().puppet.id;
  if (puppetId === null) return false;
  const label = `Resize ${plan.drawable} (${plan.fit})`;
  try {
    const fills: Command[] = [];
    for (const { tile, pixels } of plan.tiles) {
      const png = await encodePng(refitImage(pixels, plan.size, plan.offset, plan.fit));
      const image = (await deps.uploadImage(puppetId, png)).id;
      fills.push({ name: 'slot.fill', input: { slot: slotKey(plan.drawable, plan.entry, tile), image } });
    }
    const fields = { size: plan.size, ...(plan.pivotFrac === undefined ? {} : { pivotFrac: plan.pivotFrac }), slots: {} };
    const replies = await store.getState().dispatch([{ name: 'facet.setEntry', input: { drawable: plan.drawable, entry: plan.entry, fields } }, ...fills], label);
    const bad = replies.find((r) => !r.ok);
    if (bad?.ok === false) throw new Error(bad.error.message);
  } catch (e) {
    store.getState().notify('error', `${label}: ${messageOf(e)}`);
    return false;
  }
  await store.getState().refreshLint();
  await store.getState().refreshPreview();
  return true;
}
